import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { style } from 'typestyle';
import Switch from 'react-switch';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import { upsertAgendaEntry } from '../services/agenda-entry.service';
import AgendaEntryParking from '../models/agenda-entry-parking';
import { useAgendaEntryForUser } from '../hooks/agenda-entry.hooks';
import { DayPart, DayPartEntries } from '../models/daypart-entry';
import AgendaEntryParkingGroup from '../groups/agenda-entry-parking-group.component';
import DayPartGroup from '../groups/day-part-groups/day-part-group.component';
import AgendaEntryUpsert from '../models/agenda-entry-upsert';
import useRoomsForOfficeOnDate from '../hooks/room.hooks';
import { useFloorPlans } from '../hooks/floorplan.hooks';
import WorkplacesForDayPart from '../models/workplaces-for-daypart';
import type PermittedUser from '../../offices/models/permitted-user';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import { Center } from '../../../core/components/center/center.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../core/components/flex/flex.component';
import notify from '../../../core/components/notify/notify.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import { useUserForPermittedUserEmail } from '../../authentication/hooks/users.hook';
import { useOffice } from '../../offices/hooks/offices.hook';
import Department from '../../offices/models/department/department';
import type Office from '../../offices/models/office';
import RoomObsolete from '../../offices/models/room/room-obsolete';
import { useParkingsForOfficeOnDate } from '../../parkings/hooks/parkings.hook';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import RoomOccupation from '../../offices/models/room/room-occupation';
import ParkingDayPartEntries from '../models/parking-day-part-entry';
import ParkingOccupation from '../models/parking-occupation';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly permittedUser: PermittedUser;
  readonly officeId: string | undefined;
  readonly selectedDates: Date[];
  readonly handleDatesChange: (dates: Date[]) => void;
}

const CreateAgendaEntryModal = ({
  showModal,
  handleClose,
  permittedUser,
  officeId,
  selectedDates,
  handleDatesChange,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t, i18n } = useTranslation();
  const officeState = useOffice({ id: officeId });
  const [office, setOffice] = useState<Office | undefined>(undefined);
  const keycloak = useKeycloak();
  const primaryDate = selectedDates[0] ?? new Date();

  const userState = useUserForPermittedUserEmail({
    email: permittedUser.email,
  });
  const parkingState = useParkingsForOfficeOnDate({
    officeId,
    date: primaryDate.toDateString(),
  });
  const userAgendaEntryState = useAgendaEntryForUser({
    officeId,
    email: permittedUser.email,
    date: primaryDate.toDateString(),
  });

  const roomState = useRoomsForOfficeOnDate({
    officeId,
    date: primaryDate.toDateString(),
  });

  const { user } = userState;
  const agendaEntry = userAgendaEntryState.userAgendaEntry;
  const parkings = parkingState.agendaEntryParkings;
  const { rooms } = roomState;

  const [selectedDepartmentMorning, setSelectedDepartmentMorning] = useState<Department>();
  const [selectedDepartmentAfternoon, setSelectedDepartmentAfternoon] = useState<Department>();
  const [selectedRoomMorning, setSelectedRoomMorning] = useState<RoomObsolete>();
  const [selectedRoomAfternoon, setSelectedRoomAfternoon] = useState<RoomObsolete>();
  const [lunch, setLunch] = useState<boolean>(false);
  const [selectedParkingMorning, setSelectedParkingMorning] = useState<AgendaEntryParking>();
  const [selectedParkingAfternoon, setSelectedParkingAfternoon] = useState<AgendaEntryParking>();
  const [selectedWorkplaceMorningId, setSelectedWorkplaceMorningId] = useState<string | undefined>(undefined);
  const [selectedWorkplaceAfternoonId, setSelectedWorkplaceAfternoonId] = useState<string | undefined>(undefined);

  const floorPlansState = useFloorPlans({ officeId });
  const { floorPlans } = floorPlansState;

  useEffect(() => {
    if (officeState) {
      setOffice(officeState.office);
    }
  }, [officeState, setOffice]);

  useEffect(() => {
    if (rooms != null && office != null && rooms.length > 0 && rooms[0].roomOccupations != null) {
      office.rooms = roomState.rooms ?? [];
    }
  }, [roomState.rooms, office, rooms]);

  // Enrich pre-selected rooms (from agendaEntry) with full data (isReservableThroughFloorPlan, workplaces).
  // The agendaEntry API returns stripped room objects without floorplan fields.
  // We look up the matching full room from useRoomsForOfficeOnDate when available.
  useEffect(() => {
    if (agendaEntry != null && userAgendaEntryState) {
      agendaEntry.dayPartEntries?.forEach((dayPartEntry) => {
        const apiRoom = dayPartEntry.room ?? new RoomObsolete();
        const fullRoom = rooms?.find((r) => r.id === apiRoom.id);
        const roomToUse = fullRoom
          ? Object.assign(new RoomObsolete(), fullRoom)
          : Object.assign(new RoomObsolete(), apiRoom);

        switch (dayPartEntry.dayPart) {
          case DayPart.Morning: {
            setSelectedDepartmentMorning(Object.assign(dayPartEntry.department ?? new Department()));
            setSelectedRoomMorning(roomToUse);
            if (dayPartEntry.workplaceId) {
              setSelectedWorkplaceMorningId(dayPartEntry.workplaceId);
            }
            break;
          }
          case DayPart.Afternoon: {
            setSelectedDepartmentAfternoon(Object.assign(dayPartEntry.department ?? new Department()));
            setSelectedRoomAfternoon(roomToUse);
            if (dayPartEntry.workplaceId) {
              setSelectedWorkplaceAfternoonId(dayPartEntry.workplaceId);
            }
            break;
          }
          default: {
            throw new Error('DayPart Enum value path not implemented');
          }
        }
      });

      agendaEntry.parkingDayPartEntries?.forEach((parkingDayPartEntry) => {
        switch (parkingDayPartEntry.dayPart) {
          case DayPart.Morning: {
            setSelectedParkingMorning(Object.assign(parkingDayPartEntry.parking ?? new AgendaEntryParking()));
            break;
          }
          case DayPart.Afternoon: {
            setSelectedParkingAfternoon(Object.assign(parkingDayPartEntry.parking ?? new AgendaEntryParking()));
            break;
          }
          default: {
            throw new Error('DayPart Enum value path not implemented');
          }
        }
      });
      setLunch(agendaEntry.isPresentAtLunch);
    }
  }, [agendaEntry, userAgendaEntryState, rooms]);

  const handleDepartmentChange = (selectedDepartment: Department, dayPart: DayPart): void => {
    switch (dayPart) {
      case DayPart.Morning: {
        setSelectedRoomMorning(undefined);
        if (selectedDepartment.id === selectedDepartmentMorning?.id) {
          setSelectedDepartmentMorning(undefined);
          return;
        }
        setSelectedDepartmentMorning(Object.assign(selectedDepartment));
        return;
      }
      case DayPart.Afternoon: {
        setSelectedRoomAfternoon(undefined);
        if (selectedDepartment.id === selectedDepartmentAfternoon?.id) {
          setSelectedDepartmentAfternoon(undefined);
          return;
        }
        setSelectedDepartmentAfternoon(Object.assign(selectedDepartment));
        return;
      }
      default: {
        throw new Error('DayPart Enum value path not implemented');
      }
    }
  };

  const handleRoomChange = (selectedRoom: RoomObsolete, dayPart: DayPart): void => {
    switch (dayPart) {
      case DayPart.Morning: {
        if (selectedRoom.id === selectedRoomMorning?.id) {
          setSelectedRoomMorning(undefined);
          setSelectedWorkplaceMorningId(undefined);
          return;
        }
        setSelectedRoomMorning(Object.assign(selectedRoom));
        setSelectedWorkplaceMorningId(undefined);
        return;
      }
      case DayPart.Afternoon: {
        if (selectedRoom.id === selectedRoomAfternoon?.id) {
          setSelectedRoomAfternoon(undefined);
          setSelectedWorkplaceAfternoonId(undefined);
          return;
        }
        setSelectedRoomAfternoon(Object.assign(selectedRoom));
        setSelectedWorkplaceAfternoonId(undefined);
        return;
      }
      default: {
        throw new Error('DayPart Enum value path not implemented');
      }
    }
  };

  const handleParkingChange = (selectedParking: AgendaEntryParking, dayPart: DayPart): void => {
    switch (dayPart) {
      case DayPart.Morning: {
        if (selectedParking.id === selectedParkingMorning?.id) {
          setSelectedParkingMorning(undefined);
          return;
        }

        setSelectedParkingMorning(selectedParking);
        return;
      }
      case DayPart.Afternoon: {
        if (selectedParking.id === selectedParkingAfternoon?.id) {
          setSelectedParkingAfternoon(undefined);
          return;
        }

        setSelectedParkingAfternoon(selectedParking);
        return;
      }
      default: {
        throw new Error('DayPart Enum value path not implemented');
      }
    }
  };

  const handleAgendaEntryReset = (): void => {
    setSelectedDepartmentMorning(undefined);
    setSelectedDepartmentAfternoon(undefined);
    setSelectedRoomMorning(undefined);
    setSelectedRoomAfternoon(undefined);
    setSelectedParkingMorning(undefined);
    setSelectedParkingAfternoon(undefined);
    setSelectedWorkplaceMorningId(undefined);
    setSelectedWorkplaceAfternoonId(undefined);
    setLunch(false);
  };

  const handleModalClose = (): void => {
    handleDatesChange([new Date()]);
    handleAgendaEntryReset();
    handleClose();
  };

  useEffect(() => {
    handleAgendaEntryReset();
  }, [selectedDates]);

  const getRoomOccupationForIdAndDayPart = (dayPart: DayPart, roomId?: string): RoomOccupation | undefined =>
    rooms
      ?.find((room) => room.id === roomId)
      ?.roomOccupations?.find((roomOccupation: { dayPart: DayPart }) => roomOccupation.dayPart === dayPart);

  const getParkingOccupationForIdAndDayPart = (dayPart: DayPart, parkingId?: string): ParkingOccupation | undefined =>
    parkings
      ?.find((parking) => parking.id === parkingId)
      ?.parkingOccupations?.find((parkingOccupation) => parkingOccupation.dayPart === dayPart);

  const requestSuccessMessage = (): void => {
    notify(t('checkin-changes-saved-message').replace('USER_NAME', permittedUser.email), true);
  };
  // The server's own sentence when there is one — it names the actual cause, which the generic
  // fallback never could. The fallback stays for a network failure, where there is no server to ask.
  const requestFailedMessage = (reason?: string): void => {
    notify(reason ?? t('checkin-changes-saved-error-message'));
  };

  const agendaEntryValuesAreValid = (): boolean => {
    if (
      selectedRoomMorning == null &&
      selectedRoomAfternoon == null &&
      selectedParkingMorning == null &&
      selectedParkingAfternoon == null &&
      !agendaEntry?.id
    ) {
      notify(t('nothing-selected'));
      return false;
    }

    const initialMorningRoomId = agendaEntry?.dayPartEntries.find((dayPartEntry) => dayPartEntry.dayPart === DayPart.Morning)?.room?.id;
    const selectedMorningRoomOccupation = getRoomOccupationForIdAndDayPart(DayPart.Morning, selectedRoomMorning?.id);

    if (
      selectedMorningRoomOccupation != null &&
      selectedMorningRoomOccupation.freeSpaces <= 0 &&
      initialMorningRoomId !== selectedRoomMorning?.id
    ) {
      notify(t('no-workplace-available-in-room'));
      return false;
    }

    const initialAfternoonRoomId = agendaEntry?.dayPartEntries.find((dayPartEntry) => dayPartEntry.dayPart === DayPart.Afternoon)?.room?.id;
    const selectedAfternoonRoomOccupation = getRoomOccupationForIdAndDayPart(DayPart.Afternoon, selectedRoomAfternoon?.id);

    if (
      selectedAfternoonRoomOccupation != null &&
      selectedAfternoonRoomOccupation.freeSpaces <= 0 &&
      initialAfternoonRoomId !== selectedRoomAfternoon?.id
    ) {
      notify(t('no-workplace-available-in-room'));
      return false;
    }

    const initialMorningParkingDayPartEntryId = agendaEntry?.parkingDayPartEntries.find(
      (parkingDayPartEntry) => parkingDayPartEntry.dayPart === DayPart.Morning,
    )?.parking?.id;
    const selectedMorningParkingOccupation = getParkingOccupationForIdAndDayPart(DayPart.Morning, selectedParkingMorning?.id);
    if (
      selectedMorningParkingOccupation != null &&
      selectedMorningParkingOccupation.freeSpaces <= 0 &&
      initialMorningParkingDayPartEntryId !== selectedParkingMorning?.id
    ) {
      notify(t('no-parking-spot-available'));
      return false;
    }

    const initialAfternoonParkingDayPartEntryId = agendaEntry?.parkingDayPartEntries.find(
      (parkingDayPartEntry) => parkingDayPartEntry.dayPart === DayPart.Afternoon,
    )?.parking?.id;
    const selectedAfternoonParkingOccupation = getParkingOccupationForIdAndDayPart(DayPart.Afternoon, selectedParkingAfternoon?.id);
    if (
      selectedAfternoonParkingOccupation != null &&
      selectedAfternoonParkingOccupation.freeSpaces <= 0 &&
      initialAfternoonParkingDayPartEntryId !== selectedParkingAfternoon?.id
    ) {
      notify(t('no-parking-spot-available'));
      return false;
    }

    return true;
  };

  const modalIsLoading = (): boolean => !userState.initialized && !userState.error;

  // Carries the reason out of the loop below, so a failed day reports why it failed rather than
  // just that it did.
  let lastFailureReason: string | undefined;

  const createOrUpdateAgendaEntry = async (agendaEntryToUpsert: AgendaEntryUpsert): Promise<boolean> => {
    const outcome = await upsertAgendaEntry(agendaEntryToUpsert, keycloak);

    if (!outcome.saved) {
      lastFailureReason = outcome.error;
    }

    return outcome.saved;
  };

  const buildAgendaEntryForDate = (date: Date, isEditingSingleDate: boolean): AgendaEntryUpsert => {
    const agendaEntryToUpsert = new AgendaEntryUpsert();

    if (selectedRoomMorning !== undefined) {
      const newDayPartMorning = new DayPartEntries();
      newDayPartMorning.dayPart = DayPart.Morning;
      newDayPartMorning.room = selectedRoomMorning;
      newDayPartMorning.roomId = selectedRoomMorning.id;
      newDayPartMorning.department = selectedDepartmentMorning;
      newDayPartMorning.departmentId = selectedDepartmentMorning?.id;

      if (isEditingSingleDate) {
        const existingDaypartMorning = agendaEntry?.dayPartEntries.find((dayPartEntry) => dayPartEntry.dayPart === DayPart.Morning);
        if (existingDaypartMorning != null) {
          newDayPartMorning.id = existingDaypartMorning.id;
        }
      }

      agendaEntryToUpsert.dayPartEntries.push(newDayPartMorning);
    }

    if (selectedRoomAfternoon !== undefined) {
      const newDayPartAfternoon = new DayPartEntries();
      newDayPartAfternoon.dayPart = DayPart.Afternoon;
      newDayPartAfternoon.room = selectedRoomAfternoon;
      newDayPartAfternoon.roomId = selectedRoomAfternoon.id;
      newDayPartAfternoon.department = selectedDepartmentAfternoon;
      newDayPartAfternoon.departmentId = selectedDepartmentAfternoon?.id;

      if (isEditingSingleDate) {
        const existingDaypartAfternoon = agendaEntry?.dayPartEntries.find((daypart) => daypart.dayPart === DayPart.Afternoon);
        if (existingDaypartAfternoon != null) {
          newDayPartAfternoon.id = existingDaypartAfternoon.id;
        }
      }

      agendaEntryToUpsert.dayPartEntries.push(newDayPartAfternoon);
    }

    if (selectedParkingMorning !== undefined) {
      const newParkingDayPartMorning = new ParkingDayPartEntries();
      newParkingDayPartMorning.dayPart = DayPart.Morning;
      newParkingDayPartMorning.parking = selectedParkingMorning;
      newParkingDayPartMorning.parkingId = selectedParkingMorning.id;

      if (isEditingSingleDate) {
        const existingParkingDaypartMorning = agendaEntry?.parkingDayPartEntries.find(
          (parkingDayPart) => parkingDayPart.dayPart === DayPart.Morning,
        );
        if (existingParkingDaypartMorning != null) {
          newParkingDayPartMorning.id = existingParkingDaypartMorning.id;
        }
      }

      agendaEntryToUpsert.parkingDayPartEntries.push(newParkingDayPartMorning);
    }

    if (selectedParkingAfternoon !== undefined) {
      const newParkingDayPartAfternoon = new ParkingDayPartEntries();
      newParkingDayPartAfternoon.dayPart = DayPart.Afternoon;
      newParkingDayPartAfternoon.parking = selectedParkingAfternoon;
      newParkingDayPartAfternoon.parkingId = selectedParkingAfternoon.id;

      if (isEditingSingleDate) {
        const existingParkingDaypartAfternoon = agendaEntry?.parkingDayPartEntries.find(
          (parkingDayPart) => parkingDayPart.dayPart === DayPart.Afternoon,
        );
        if (existingParkingDaypartAfternoon != null) {
          newParkingDayPartAfternoon.id = existingParkingDaypartAfternoon.id;
        }
      }

      agendaEntryToUpsert.parkingDayPartEntries.push(newParkingDayPartAfternoon);
    }

    if (isEditingSingleDate) {
      agendaEntryToUpsert.id = agendaEntry?.id;
    }
    agendaEntryToUpsert.officeId = office?.id;
    agendaEntryToUpsert.date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    agendaEntryToUpsert.isPresentAtLunch = lunch;
    agendaEntryToUpsert.isPresentAtWork = true;
    agendaEntryToUpsert.user = user;
    agendaEntryToUpsert.alternativeTargetEmail = user?.email;

    const workplacesForDayparts: WorkplacesForDayPart[] = [];
    if (selectedWorkplaceMorningId) {
      const morningWorkplace = new WorkplacesForDayPart();
      morningWorkplace.dayPart = DayPart.Morning;
      morningWorkplace.workplaceIds = [selectedWorkplaceMorningId];
      workplacesForDayparts.push(morningWorkplace);
    }
    if (selectedWorkplaceAfternoonId) {
      const afternoonWorkplace = new WorkplacesForDayPart();
      afternoonWorkplace.dayPart = DayPart.Afternoon;
      afternoonWorkplace.workplaceIds = [selectedWorkplaceAfternoonId];
      workplacesForDayparts.push(afternoonWorkplace);
    }
    if (workplacesForDayparts.length > 0) {
      agendaEntryToUpsert.workplacesForDayparts = workplacesForDayparts;
    }

    return agendaEntryToUpsert;
  };

  const handleSave = async (): Promise<void> => {
    if (office == null) {
      return;
    }
    if (!agendaEntryValuesAreValid()) {
      return;
    }

    const isEditingSingleDate = selectedDates.length === 1;

    if (
      selectedRoomMorning === undefined &&
      selectedRoomAfternoon === undefined &&
      selectedParkingMorning === undefined &&
      setSelectedParkingAfternoon === undefined
    ) {
      if (isEditingSingleDate && agendaEntry?.id === undefined) {
        requestSuccessMessage();
        handleClose();
        return;
      }

      if (isEditingSingleDate) {
        const entry = buildAgendaEntryForDate(primaryDate, true);
        const success = await createOrUpdateAgendaEntry(entry);
        if (success) {
          requestSuccessMessage();
          handleModalClose();
        } else {
          requestFailedMessage(lastFailureReason);
        }
        return;
      }
    }

    const results = await Promise.all(
      selectedDates.map((date) => {
        const entry = buildAgendaEntryForDate(date, isEditingSingleDate);
        return createOrUpdateAgendaEntry(entry);
      }),
    );
    const allSucceeded = results.every((success) => success);

    if (allSucceeded) {
      requestSuccessMessage();
      handleModalClose();
    } else {
      requestFailedMessage(lastFailureReason);
    }
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      {((): React.JSX.Element => {
        if (modalIsLoading() || office == null) {
          return (
            <Center>
              <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
            </Center>
          );
        }

        if (userState.error) {
          return (
            <Column itemSpacing={20}>
              <span className={style(styles.modalTitle)}>{t('user-has-not-logged-in-message')}</span>
              <Center className={style(styles.button)}>
                <PrimaryButton onClick={handleModalClose}>{t('cancel')}</PrimaryButton>
              </Center>
            </Column>
          );
        }

        return (
          <Column mainAxisAlignment={MainAxisAlignment.Start} itemSpacing={20}>
            <span className={style(styles.columnHeader)}>
              {t('employee').toUpperCase()}: {user?.name ?? user?.email}
            </span>
            <Row>
              <span className={style(styles.dateText)}>
                {selectedDates.length <= 1
                  ? `${t('selected-date')}: ${primaryDate.toLocaleDateString(i18n.language)}`
                  : [
                    t('selected-dates'),
                    ': ',
                    selectedDates[0].toLocaleDateString(i18n.language),
                    ' - ',
                    selectedDates[selectedDates.length - 1].toLocaleDateString(i18n.language),
                    ` (${selectedDates.length} ${t('day(s)')})`,
                  ].join('')}
              </span>
            </Row>
            <DayPartGroup
              title={t('morning').toUpperCase()}
              dayPart={DayPart.Morning}
              office={office}
              rooms={rooms}
              selectedDepartment={selectedDepartmentMorning}
              selectedRoom={selectedRoomMorning}
              onDepartmentSelectionChanged={(value): void => {
                handleDepartmentChange(value, DayPart.Morning);
              }}
              onRoomSelectionChanged={(value): void => {
                handleRoomChange(value, DayPart.Morning);
              }}
              officeId={officeId}
              selectedDates={selectedDates}
              floorPlans={floorPlans}
              selectedWorkplaceId={selectedWorkplaceMorningId}
              onWorkplaceSelected={(wpId): void => {
                setSelectedWorkplaceMorningId(wpId || undefined);
              }}
            />
            <AgendaEntryParkingGroup
              parkings={parkings}
              dayPart={DayPart.Morning}
              onParkingSelectionChanged={handleParkingChange}
              selectedParking={selectedParkingMorning}
            />
            {office.lunchEnabled && (
              <Row>
                <span className={style(styles.preText)}>{t('lunch')}</span>
                <Switch
                  checked={lunch}
                  onChange={(): void => {
                    setLunch(!lunch);
                  }}
                  onColor={StyleConstants.ORANGE_COLOR}
                  onHandleColor={StyleConstants.WHITE_COLOR}
                  handleDiameter={styles.switchDiameter}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  boxShadow={styles.switchBoxShadow}
                  activeBoxShadow={styles.switchActiveBoxShadow}
                  height={styles.switchHeight}
                  width={styles.switchWidth}
                  className="react-switch"
                  id="material-switch"
                />
              </Row>
            )}
            <DayPartGroup
              title={t('afternoon').toUpperCase()}
              dayPart={DayPart.Afternoon}
              office={office}
              rooms={rooms}
              selectedDepartment={selectedDepartmentAfternoon}
              selectedRoom={selectedRoomAfternoon}
              onDepartmentSelectionChanged={(value): void => {
                handleDepartmentChange(value, DayPart.Afternoon);
              }}
              onRoomSelectionChanged={(value): void => {
                handleRoomChange(value, DayPart.Afternoon);
              }}
              officeId={officeId}
              selectedDates={selectedDates}
              floorPlans={floorPlans}
              selectedWorkplaceId={selectedWorkplaceAfternoonId}
              onWorkplaceSelected={(wpId): void => {
                setSelectedWorkplaceAfternoonId(wpId || undefined);
              }}
            />
            <AgendaEntryParkingGroup
              parkings={parkings}
              dayPart={DayPart.Afternoon}
              onParkingSelectionChanged={handleParkingChange}
              selectedParking={selectedParkingAfternoon}
            />
            <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
              <div className={style(styles.button)}>
                <PrimaryButton
                  fullWidth
                  onClick={(): void => {
                    handleSave();
                  }}
                >
                  {t('save')}
                </PrimaryButton>
              </div>
              <div className={style(styles.button)}>
                <PrimaryButton fullWidth onClick={handleModalClose}>
                  {t('cancel')}
                </PrimaryButton>
              </div>
            </Row>
          </Column>
        );
      })()}
    </Modal>
  );
};

export default CreateAgendaEntryModal;
